import { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Search, Compass, Plus } from "lucide-react";
import { useI18n } from "../i18n";
import ListenButton from "./ListenButton";

const CARDS = [
  { img: "https://images.unsplash.com/photo-1496442226666-8d4d0e62e6e9?w=500&q=70", title: "New York", sub: "USA" },
  { img: "https://images.unsplash.com/photo-1493976040374-85c8e12f0c0e?w=500&q=70", title: "Kyoto", sub: "Japan" },
  { img: "https://images.unsplash.com/photo-1580060839134-75a5edca2e99?w=500&q=70", title: "Cape Town", sub: "South Africa" },
  { img: "https://images.unsplash.com/photo-1679311562700-5677ad2cc7cf?w=500&q=70", title: "Kerala", sub: "India" },
  { img: "https://images.unsplash.com/photo-1613395877344-13d4a8e0d49e?w=500&q=70", title: "Santorini", sub: "Greece" },
  { img: "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=500&q=70", title: "Rarotonga", sub: "Cook Islands" },
];

export default function PortalHero({ onSearch }) {
  const { tr } = useI18n();
  const [q, setQ] = useState("");

  const heroText = `${tr("Discover the World, One Community at a Time.")} ${tr("Explore countries, states, counties, cities and villages.")} ${tr("Discover local stories, people, places, culture and opportunities.")}`;

  const submit = (e) => {
    e.preventDefault();
    if (q.trim()) onSearch(q.trim());
  };

  return (
    <section id="portal-hero" data-testid="portal-hero-section" className="relative overflow-hidden" style={{ background: "linear-gradient(135deg, #EAF4FD 0%, #F6FAFF 45%, #DCEDFB 100%)" }}>
      <div className="max-w-[1400px] mx-auto px-4 lg:px-8 py-14 lg:py-20 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <motion.h1
            data-testid="portal-hero-heading"
            className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-[#0F2557] leading-[1.05]"
            initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            {tr("Discover the World,")}<br />{tr("One Community at a Time.")}
          </motion.h1>
          <motion.p
            className="text-base md:text-lg text-[#475569] mt-5 max-w-xl leading-relaxed"
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15, duration: 0.8 }}
          >
            {tr("Explore countries, states, counties, cities and villages.")}<br />
            {tr("Discover local stories, people, places, culture and opportunities.")}
          </motion.p>

          <motion.form
            onSubmit={submit}
            className="mt-8 flex items-center bg-white rounded-2xl shadow-lg shadow-blue-100 border border-[#DCE8F7] p-2 max-w-xl"
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.8 }}
          >
            <MapPin size={20} className="text-[#2563EB] ml-3 shrink-0" />
            <input
              data-testid="hero-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={tr("Search any country, state, county, city or village")}
              className="flex-1 px-3 py-3 text-[#0F2557] placeholder:text-[#94A3B8] focus:outline-none text-sm md:text-base min-w-0"
            />
            <button data-testid="hero-search-button" type="submit" className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-bold px-5 md:px-7 py-3 rounded-xl flex items-center gap-2 transition-colors duration-200 active:scale-[0.97]">
              <Search size={16} /><span className="hidden sm:inline">{tr("Search")}</span>
            </button>
          </motion.form>

          <motion.div
            className="flex flex-wrap items-center gap-3 mt-7"
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45, duration: 0.8 }}
          >
            <button
              data-testid="explore-world-button"
              onClick={() => document.getElementById("worldmap")?.scrollIntoView({ behavior: "smooth" })}
              className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-bold px-7 py-3.5 rounded-2xl flex items-center gap-2.5 shadow-lg shadow-blue-200 transition-all duration-200 active:scale-[0.97]"
            >
              <Compass size={18} /> {tr("Explore the World")} →
            </button>
            <button
              data-testid="add-place-button"
              onClick={() => document.getElementById("waitlist")?.scrollIntoView({ behavior: "smooth" })}
              className="border-2 border-[#2563EB] text-[#2563EB] hover:bg-[#EAF2FD] font-bold px-6 py-3 rounded-2xl flex items-center gap-2 transition-colors duration-200 active:scale-[0.97]"
            >
              <Plus size={18} /> {tr("Add Your Place")}
            </button>
            <ListenButton text={heroText} label={tr("Listen")} />
          </motion.div>
        </div>

        <div className="relative">
          <span className="hidden lg:block absolute -left-6 top-2 font-hand text-2xl text-[#1D4ED8] rotate-[-8deg] leading-tight z-10">
            People Places<br />Stronger Together
          </span>
          <span className="hidden lg:block absolute -right-2 bottom-4 font-hand text-2xl text-[#0EA5E9] rotate-[6deg] z-10">
            A More<br />Connected World
          </span>
          <div className="grid grid-cols-3 gap-3 max-w-lg mx-auto">
            {CARDS.map((c, i) => (
              <motion.div
                key={c.title}
                data-testid={`hero-place-card-${i}`}
                className={`relative rounded-2xl overflow-hidden shadow-md group ${i === 0 || i === 3 ? "row-span-2 h-52 md:h-64" : "h-24 md:h-[7.6rem]"}`}
                initial={{ opacity: 0, scale: 0.9, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ delay: 0.2 + i * 0.1, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              >
                <img src={c.img} alt={c.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0F2557]/60 via-transparent to-transparent" />
                <div className="absolute bottom-2 left-2.5 text-white">
                  <div className="text-xs md:text-sm font-bold leading-tight">{c.title}</div>
                  <div className="text-[0.6rem] md:text-[0.65rem] opacity-80">{c.sub}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
